import { useEffect, useRef, useState, Suspense, lazy } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../lib/api-client';
import { env } from '../../lib/env';
import { useSetPageHeader } from '../../components/app/AppShell';
import { Spinner } from '../../components/kit/Spinner';
import { haptic } from '../../lib/haptic';
import {
  getCachedVehicleDoc,
  putCachedVehicleDoc,
  type VehicleDocType,
} from '../../lib/vehicle-doc-cache';

const LazyDriverPdfViewer = lazy(() => import('../../components/form/DriverPdfViewer'));

type Slug = 'insurance' | 'puc' | 'fitness' | 'permit' | 'road-tax' | 'registration';

const SLUG_TO_TYPE: Record<Slug, VehicleDocType> = {
  'insurance': 'INSURANCE',
  'puc': 'PUC',
  'fitness': 'FITNESS',
  'permit': 'PERMIT',
  'road-tax': 'ROAD_TAX',
  'registration': 'REGISTRATION',
};

const TITLE_KEY: Record<VehicleDocType, string> = {
  INSURANCE: 'vehicleDoc.insuranceTitle',
  PUC: 'vehicleDoc.pucTitle',
  FITNESS: 'vehicleDoc.fitnessTitle',
  PERMIT: 'vehicleDoc.permitTitle',
  ROAD_TAX: 'vehicleDoc.roadTaxTitle',
  REGISTRATION: 'vehicleDoc.registrationTitle',
};

type ComplianceDoc = { type: string; updatedAt: string; filename: string | null };

type VehicleResp = {
  vehicle: {
    id: string;
    regNumber: string;
    complianceDocs: ComplianceDoc[];
    // … other fields ignored here; full type lives in VehicleRoute.tsx
  } | null;
};

type State =
  | { kind: 'loading' }
  | { kind: 'ready'; mime: string; objectUrl: string }
  | { kind: 'empty' }
  | { kind: 'no-assignment' }
  | { kind: 'error'; message: string };

export function VehicleDocViewerRoute() {
  const { type: slug } = useParams<{ type: Slug }>();
  const { t } = useTranslation('vehicle');
  const docType: VehicleDocType | undefined = slug ? SLUG_TO_TYPE[slug] : undefined;

  useSetPageHeader(
    docType ? { backTo: '/vehicle', title: t(TITLE_KEY[docType]) } : null,
    [docType],
  );

  const [state, setState] = useState<State>({ kind: 'loading' });
  const objectUrlRef = useRef<string | null>(null);
  // Race-cancellation token. PR #252 commit 83009c36 lesson — every awaited
  // boundary must check that this load() hasn't been superseded by a later
  // one (e.g., a Refresh tap mid-load). Without this, a late resolve could
  // revoke the newer load's objectURL and overwrite fresh bytes with stale.
  const loadTokenRef = useRef(0);

  const showBlob = (mime: string, blob: Blob) => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    const url = URL.createObjectURL(blob);
    objectUrlRef.current = url;
    setState({ kind: 'ready', mime, objectUrl: url });
  };

  const load = async (force = false) => {
    if (!docType) return;
    const myToken = ++loadTokenRef.current;
    setState({ kind: 'loading' });

    try {
      const me = await apiFetch<VehicleResp>('/driver-app/me/vehicle');
      if (myToken !== loadTokenRef.current) return; // superseded
      if (!me.vehicle) { setState({ kind: 'no-assignment' }); return; }
      const vehicleId = me.vehicle.id;
      const docMeta = me.vehicle.complianceDocs.find(d => d.type === docType);
      if (!docMeta) { setState({ kind: 'empty' }); return; }
      const updatedAtMs = new Date(docMeta.updatedAt).getTime();

      const cached = !force ? await getCachedVehicleDoc(vehicleId, docType) : null;
      if (myToken !== loadTokenRef.current) return; // superseded
      if (cached && cached.cachedAt >= updatedAtMs) {
        showBlob(cached.mime, cached.blob);
        return;
      }

      const apiBase = env().VITE_API_BASE.replace(/\/+$/, '');
      const res = await fetch(`${apiBase}/v1/driver-app/me/vehicle/compliance/${slug}/image`, { credentials: 'include' });
      if (myToken !== loadTokenRef.current) return; // superseded
      if (res.status === 404) { setState({ kind: 'empty' }); return; }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const mime = res.headers.get('content-type') ?? 'application/octet-stream';
      const blob = await res.blob();
      if (myToken !== loadTokenRef.current) return; // superseded
      await putCachedVehicleDoc(vehicleId, docType, mime, blob);
      if (myToken !== loadTokenRef.current) return; // superseded
      showBlob(mime, blob);
    } catch (e: unknown) {
      if (myToken !== loadTokenRef.current) return; // superseded — don't surface stale errors
      const message = e instanceof Error ? e.message : String(e);
      setState({ kind: 'error', message });
    }
  };

  useEffect(() => {
    void load();
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docType]);

  if (!docType) return null;

  const onRefresh = async () => {
    haptic.tap();
    // We need vehicleId to invalidate — re-fetch /me/vehicle implicit in load(true).
    // For surgical invalidation pre-refetch, we'd need to track vehicleId in state;
    // simpler to let load(true) do its thing with the cache flag.
    await load(true);
  };

  return (
    <div className="px-5 pt-2 pb-10">
      <div className="flex justify-end mb-2">
        <button
          type="button"
          onClick={onRefresh}
          className="text-sm font-semibold text-[var(--green-700)]"
        >
          {t('vehicleDoc.viewerRefresh')}
        </button>
      </div>

      {state.kind === 'loading' ? (
        <div className="flex flex-col items-center gap-3 py-12">
          <Spinner size={32} />
          <p className="text-sm text-[var(--color-text-secondary)]">{t('vehicleDoc.viewerLoading')}</p>
        </div>
      ) : null}

      {state.kind === 'empty' ? (
        <div className="py-12 text-center text-[var(--color-text-secondary)]">{t('vehicleDoc.viewerEmpty')}</div>
      ) : null}

      {state.kind === 'no-assignment' ? (
        <div className="py-12 text-center text-[var(--color-text-secondary)]">{t('vehicleDoc.viewerNoAssignment')}</div>
      ) : null}

      {state.kind === 'error' ? (
        <div className="py-12 text-center">
          <p className="text-sm text-[var(--color-text-secondary)]">{t('vehicleDoc.viewerError')}</p>
          <p className="mt-2 text-xs text-[var(--color-text-secondary)] opacity-70">{state.message}</p>
        </div>
      ) : null}

      {state.kind === 'ready' && state.mime.startsWith('image/') ? (
        <img
          src={state.objectUrl}
          alt={t(TITLE_KEY[docType])}
          style={{ width: '100%', maxHeight: '85vh', objectFit: 'contain' }}
        />
      ) : null}

      {state.kind === 'ready' && state.mime === 'application/pdf' ? (
        <Suspense fallback={<Spinner size={32} />}>
          <LazyDriverPdfViewer file={state.objectUrl} />
        </Suspense>
      ) : null}
    </div>
  );
}
